import { useNavigation } from "@react-navigation/native";
import { Calendar, Trash2, Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { getDayJsDate } from "../../../core/dayJs";
import { Block } from "../../../ui/Block";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import type { InfoRowItemProps } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import {
  type BusinessPlanModel,
  BusinessStatus,
  BusinessType,
} from "../../plan/BusinessPlanModel";
import type { BusinessCustomerModel } from "../customers/BusinessCustomerModel";
import { BusinessInfoRow } from "./BusinessInfoRow";

const EventRow = ({ event }: { event: BusinessPlanModel }) => {
  const navigation = useNavigation();

  const infoRowItems: InfoRowItemProps[] = [
    {
      icon: Calendar,
      text: `${getDayJsDate(event.data.eventInfo.date)}`,
    },
    {
      icon: Users,
      text: `${event.usersCount} users`,
    },
  ];

  return (
    <Block
      grouped
      infoRowItems={infoRowItems}
      onPress={() => {
        navigation.navigate("BusinessHome", {
          businessUid: event.uid,
        });
      }}
    >
      <XStack gap={"$medium"} justifyContent={"space-between"} width={"100%"}>
        <Label>{event.data.planInfo.title}</Label>

        <PopoverConfirm
          onConfirm={() => {
            event.updateStatus(
              event.data.status === BusinessStatus.Archived
                ? BusinessStatus.Active
                : BusinessStatus.Archived,
            );
          }}
        >
          <Trash2 size={"$size.normal"} color={"$muted"} />
        </PopoverConfirm>
      </XStack>
    </Block>
  );
};

export const BusinessAdminEventsList = observer(
  ({ customer }: { customer: BusinessCustomerModel }) => {
    const { businessPlansApi } = useApi();

    const _navigation = useNavigation();

    React.useEffect(() => {
      void businessPlansApi.getPlansForCustomer(customer.uid);
    }, [businessPlansApi, customer]);

    const events: BusinessPlanModel[] = customer.activeBusinessPlans;

    const createPlan = React.useCallback(
      (title: string) => {
        void businessPlansApi.add({
          status: BusinessStatus.Active,
          type: BusinessType.Regular,
          customerInfo: {
            uid: customer.uid,
            ...customer.data.profile,
          },
          profileQuestions: [],
          feedCount: 0,
          teamsInfo: [],
          feedMediaCount: 0,
          feedLatestEvents: [],
          feedPinnedEvents: [],
          eventInfo: {
            tags: [],
            tasks: [],
            date: null,
            settings: null,
          },
          planInfo: {
            title,
            emoji: "😆",
            backgroundColor: null,
            descriptionYooptaEditor: null,
            coverUrl: null,
          },
          sections: [],
          userInfo: {},
        });
      },
      [businessPlansApi, customer],
    );

    return (
      <View>
        <Box gap={"$large"}>
          <BusinessInfoRow
            title={
              "This is your events, sorted by the most recent at the top. Add new events by using your Templates."
            }
          />
          <Label>Active Events ({events.length})</Label>

          <Group>
            {events.map((event) => (
              <EventRow key={event.uid} event={event} />
            ))}
          </Group>
        </Box>
        <Button
          type={"ghost"}
          onPress={() => {
            createPlan(`My Event ${Date.now()}`);
          }}
        >
          Add Empty/Blank Event
        </Button>
      </View>
    );
  },
);
